import { Button, Chip } from '@nextui-org/react'
import { FC, useCallback, useMemo } from 'react'
import { MdFileDownload } from 'react-icons/md'
import ApiStateView from '../ApiState'
// import KpiCard from '../KpiCard'
import LogiTable from '../LogiTable'
import Panel from '../Panel'
import SectionTitle from '../SectionTitle'
import { TONES } from '../../constants'
// import { useLogisticBox } from '../../context/LogisticBoxContext' // se usaba para "Ver equipos" (comentado)
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { incumplimientosApi, IncumplimientoStoreRow, InventoryCell } from '../../api'
import { ToneKey } from '../../models'

type ChipColor = 'success' | 'warning' | 'danger' | 'primary' | 'secondary' | 'default'

const chipColor = (tone: ToneKey): ChipColor => {
  if (tone === 'green') return 'success'
  if (tone === 'amber') return 'warning'
  if (tone === 'rosa') return 'danger'
  if (tone === 'blue') return 'primary'
  if (tone === 'lila') return 'secondary'
  return 'default'
}

const STOCK_COLS: Array<{ codigo: InventoryCell['codigo'], label: string }> = [
  { codigo: 'caja', label: 'Cajas' },
  { codigo: 'bolsa_a', label: 'Bolsas A' },
  { codigo: 'bolsa_b', label: 'Bolsas B' },
  { codigo: 'sello', label: 'Sellos' }
]

const StockCell: FC<{ cell: InventoryCell | undefined }> = ({ cell }) => {
  if (cell === undefined) return <span className='text-xs text-gray-300'>—</span>
  const color = cell.bajo_safety ? TONES.rosa.ink : '#151930'
  return (
    <span className='text-xs font-semibold' style={{ color }}>
      {cell.cantidad_actual}
      <span className='text-[10px] font-normal text-gray-400'> / {cell.safety_stock}</span>
    </span>
  )
}

const columns = [
  { name: 'Tienda', uid: 'store' },
  { name: 'Entregados', uid: 'entregados', align: 'center' as const },
  { name: 'Custodia', uid: 'custodia', align: 'center' as const },
  ...STOCK_COLS.map(c => ({ name: c.label, uid: `stock_${c.codigo}`, align: 'center' as const })),
  { name: 'Sin retiro', uid: 'dias_sin_retiro' },
  { name: 'Causas', uid: 'causas' },
  { name: '', uid: 'actions' }
]

const IncumplimientosView: FC = () => {
  // goToDetail se usaba para el botón "Ver equipos" (comentado actualmente).
  // const { goToDetail } = useLogisticBox()
  const idSeller = useSelectedSeller()

  const fetcher = useCallback(async () => await incumplimientosApi.list(idSeller), [idSeller])
  const data = useApi(fetcher, [idSeller])

  /* Sección de KPIs comentada en el render · cálculo comentado (noUnusedLocals)
  const kpis: KpiCardData[] = useMemo(() => {
    const k = data.data?.kpis
    return [
      {
        value: k?.tiendas_con_atraso ?? 0,
        label: 'Tiendas con incumplimiento',
        tone: 'rosa' as ToneKey,
        hint: 'Reglas no cumplidas, errores o bloqueos'
      },
      {
        value: k?.equipos_atrasados ?? 0,
        label: 'Equipos atrasados',
        tone: 'amber' as ToneKey,
        hint: 'Entregados o en custodia sin retiro'
      }
      // {
      //   value: k?.dias_promedio ?? 0,
      //   label: 'Días promedio',
      //   tone: 'foundation',
      //   hint: k?.equipo_mas_antiguo_dias !== null && k?.equipo_mas_antiguo_dias !== undefined
      //     ? `Equipo más antiguo: ${k.equipo_mas_antiguo_dias} días`
      //     : undefined
      // },
      // {
      //   value: k?.tiendas_sin_direccion ?? 0,
      //   label: 'Sin dirección',
      //   tone: 'rosa' as ToneKey,
      //   hint: 'Con equipos en custodia pero sin dirección · no se pueden recolectar'
      // }
    ]
  }, [data.data])
  */

  const rows = useMemo(() => data.data?.rows ?? [], [data.data])

  const renderCell = (i: IncumplimientoStoreRow, columnKey: string): JSX.Element => {
    if (columnKey.startsWith('stock_')) {
      const codigo = columnKey.slice('stock_'.length)
      return <StockCell cell={i.stock?.find(s => s.codigo === codigo)} />
    }
    switch (columnKey) {
      case 'store':
        return (
          <div className='font-medium text-xs text-[#151930]'>
            <div>{i.store}</div>
            {i.sin_direccion && (
              <span className='inline-block mt-0.5 text-[10px] text-[#A32D2D] bg-[#FCEBEB] px-1.5 py-0.5 rounded'>
                ● Sin dirección
              </span>
            )}
          </div>
        )
      case 'entregados':
        return (
          <span className='text-xs font-semibold' style={{ color: TONES[i.diagnostico.tone].ink }}>
            {i.entregados}
          </span>
        )
      case 'custodia':
        return (
          <span className='text-xs font-semibold' style={{ color: TONES[i.diagnostico.tone].ink }}>
            {i.custodia}
          </span>
        )
      case 'dias_sin_retiro': {
        const dias = i.dias_sin_retiro
        const diasColor = dias !== null && dias > 14 ? TONES.rosa.ink : TONES.amber.ink
        return dias !== null
          ? <span className='text-xs font-semibold' style={{ color: diasColor }}>{dias} días</span>
          : <span className='text-[14px] text-gray-400'>nunca retirada</span>
      }
      case 'causas':
        return (
          <div className='flex flex-col gap-1 items-start'>
            {i.causas.map((c, idx) => (
              <Chip key={idx} size='sm' variant='flat' color={chipColor(c.tone)}>{c.label}</Chip>
            ))}
          </div>
        )
      case 'actions':
        return (
          <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
            {/* Botón "Ver equipos" comentado · se conserva por si se reactiva
            <Button
              size='sm'
              className='bg-black text-white text-xs h-8'
              onPress={() => goToDetail('tienda-atrasada', i.store, i.id_store)}
            >
              Ver equipos
            </Button>
            */}
          </div>
        )
      default:
        return <span className='text-xs'>{(i as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      {/* <section className='grid grid-cols-2 md:grid-cols-4 gap-4'>
        {kpis.map((k, i) => <KpiCard key={i} data={k} />)}
      </section> */}

      <Panel>
        <div className='flex items-center justify-between mb-4 flex-wrap gap-3'>
          <SectionTitle title='Tiendas con incumplimientos' />
          <Button variant='bordered' className='border-gray-200 text-xs' startContent={<MdFileDownload className='text-base' />}>Exportar</Button>
        </div>

        <p className='text-[14px] text-[#151930]/50 mb-4'>
          Tiendas que no cumplen sus reglas de recolección (límite inmediato, semanal o de días según la configuración de cada tienda),
          con errores de etiqueta DHL, cajas atrasadas o bloqueos por falta de dirección.
        </p>

        <ApiStateView
          loading={data.loading}
          error={data.error}
          onRetry={data.reload}
          empty={!data.loading && (data.data?.rows.length ?? 0) === 0}
          emptyText='Sin tiendas con incumplimientos en este rango'
        >
          <LogiTable
            ariaLabel='Tiendas con incumplimientos'
            columns={columns}
            items={rows}
            rowKey={(i: IncumplimientoStoreRow) => i.id_store}
            loading={data.loading}
            emptyText='Sin tiendas con incumplimientos en este rango'
            rowsPerPage={10}
            renderCell={renderCell}
            bottomExtra={
              <div className='flex items-center justify-between text-xs text-[#151930]/60 flex-wrap gap-3'>
                <span>
                  Mostrando {data.data?.rows.length ?? 0} tiendas con incumplimientos · {data.data?.kpis.equipos_atrasados ?? 0} equipos
                </span>
                <div className='flex gap-4'>
                  <span className='flex items-center gap-1'>
                    <span className='w-2 h-2 rounded-full' style={{ background: TONES.amber.ink }} />
                    Atención
                  </span>
                  <span className='flex items-center gap-1'>
                    <span className='w-2 h-2 rounded-full' style={{ background: TONES.rosa.ink }} />
                    Crítico
                  </span>
                </div>
              </div>
            }
          />
        </ApiStateView>
      </Panel>
    </div>
  )
}

export default IncumplimientosView
